import { Directive, HostListener, Input, Output, ElementRef, EventEmitter, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { DataService } from '../service/data.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CacheService } from '../service/cache.service';

@Directive({
	selector: '[textHover]'
})
export class TextHoverDirective implements OnInit, OnDestroy {
	private static activeHighlight: TextHoverDirective | null = null;

	private lastChar: string | null = null;
	private originalText: string | null = null;
	private currentHighlightedWord: string | null = null;
	private hoverSubject: Subject<MouseEvent>;
	private highlightElement: HTMLElement | null = null;
	private cleanupFn: () => void;
	private parts: any = [];
	@Input() disable: boolean = false;

	constructor(
		private el: ElementRef,
		private datas: DataService,
		private renderer: Renderer2,
		public caches: CacheService
	) { }

	@Output() public hoverCallback: EventEmitter<any> = new EventEmitter<any>();
	@Output() public leaveCallback: EventEmitter<any> = new EventEmitter<any>();
	@Output() public textHoverClick: EventEmitter<any> = new EventEmitter<any>();

	ngOnInit() {
		if (this.disable) return;

		this.hoverSubject = new Subject();
		this.hoverSubject.pipe(debounceTime(50)).subscribe(event => {
			this.handleMouseMove(event);
		});

		this.originalText = this.el.nativeElement.innerHTML;

		this.highlightElement = this.renderer.createElement('div');
		this.renderer.addClass(this.highlightElement, 'text-highlight');
		this.renderer.setStyle(this.highlightElement, 'position', 'absolute');
		this.renderer.setStyle(this.highlightElement, 'pointer-events', 'none');
		this.renderer.setStyle(this.highlightElement, 'z-index', '1000');
		this.renderer.setStyle(this.highlightElement, 'display', 'none');
		this.renderer.appendChild(document.body, this.highlightElement);

		this.cleanupFn = () => {
			if (this.highlightElement) {
				this.renderer.removeChild(document.body, this.highlightElement);
			}
		};
	}

	ngOnDestroy() {
		if (this.disable) return;
		if (this.cleanupFn) {
			this.cleanupFn();
		}
		if (this.hoverSubject) {
			this.hoverSubject.unsubscribe();
		}
		if (TextHoverDirective.activeHighlight === this) {
			TextHoverDirective.activeHighlight = null;
		}
	}

	@HostListener('mousemove', ['$event']) onMouseMove(event: MouseEvent): void {
		if (this.disable) return;
		this.hoverSubject.next(event);
	}

	@HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent): void {
		if (this.disable) return;
		this.removeHighlight();
		const leaveData = this.getLeaveData(event);
		if (leaveData) {
			this.leaveCallback.emit(leaveData);
		}
		this.lastChar = null;
	}

	@HostListener('click', ['$event']) onClick(event: MouseEvent): void {
		if (this.disable) return;
		this.handleClick(event.clientX, event.clientY);
	}

	// Mobile 触摸事件监听
	@HostListener('touchmove', ['$event']) onTouchMove(event: TouchEvent): void {
		if (this.disable) return;
		event.preventDefault();
		const touch = event.touches[0];
		this.hoverSubject.next({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent);
	}

	@HostListener('touchend', ['$event']) onTouchEnd(event: TouchEvent): void {
		if (this.disable) return;
		const touch = event.changedTouches[0];
		const element = document.elementFromPoint(touch.clientX, touch.clientY);
		if (!element || !this.el.nativeElement.contains(element)) {
			this.removeHighlight();
			this.leaveCallback.emit(this.getLeaveData(event));
			this.lastChar = null;
		}
	}

	@HostListener('touchstart', ['$event']) onTouchStart(event: TouchEvent): void {
		if (this.disable) return;
		const touch = event.touches[0];
		setTimeout(() => this.handleClick(touch.clientX, touch.clientY), 100);
	}

	private handleClick(x: number, y: number): void {
		// 1. 获取精准的字符信息（包含字符本身、全局偏移、视觉位置）
		const charInfo = this.getCharacterAtPointWithVisualPosition(x, y);
		if (!charInfo || charInfo.character === '' || charInfo.globalOffset === -1) return;

		// 2. 获取清理后的完整文本（保留原始字符顺序，包含换行但去重）
		const fullText = this.el.nativeElement.innerText.replace(/\s+/g, ' ').trim();
		// 安全校验：确保全局偏移量在文本范围内
		if (charInfo.globalOffset >= fullText.length) return;

		// 3. 获取点击的原始字符（从完整文本中精准取，避免字符识别错误）
		const clickedChar = fullText[charInfo.globalOffset] || '';
		if (!clickedChar) return;

		// 4. 基于“字符归属”精准匹配NLP词语
		let targetWord = '';
		const matchedWords: { word: string, wordIndex: number, distance: number }[] = [];

		if (this.parts?.length > 0 && fullText) {
			for (const part of this.parts) {
				const word = part.text.trim();
				if (!word) continue;
				const wordLength = word.length;

				// 遍历该词语的所有出现位置
				let wordIndex = fullText.indexOf(word);
				while (wordIndex !== -1) {
					// 核心校验（三重验证）：
					// a. 字符偏移量在词语范围内
					// b. 该字符确实是词语的一部分（而非仅偏移量重叠）
					// c. 视觉位置匹配（严格）
					const charInWordIndex = charInfo.globalOffset - wordIndex;
					const isCharInWord = charInWordIndex >= 0 && charInWordIndex < wordLength;
					const isCharMatched = isCharInWord && word[charInWordIndex] === clickedChar;

					if (isCharInWord && isCharMatched) {
						// 获取词语的视觉位置
						const wordRect = this.getWordVisualRect(word, wordIndex);
						if (wordRect) {
							// 计算点击点到词语的距离（用于排序）
							const distance = this.calculateDistanceToRect(x, y, wordRect);
							// 严格视觉匹配：点击点必须在词语矩形内
							if (x >= wordRect.left && x <= wordRect.right && y >= wordRect.top && y <= wordRect.bottom) {
								matchedWords.push({ word, wordIndex, distance });
							}
						}
					}
					wordIndex = fullText.indexOf(word, wordIndex + wordLength);
				}
			}

			// 5. 选择最优匹配（距离最近的词语）
			if (matchedWords.length > 0) {
				// 按距离排序，取最近的
				matchedWords.sort((a, b) => a.distance - b.distance);
				targetWord = matchedWords[0].word;
			}
		}

		// 6. 降级方案：无匹配时返回点击的原始字符
		if (!targetWord) {
			targetWord = clickedChar;
		}

		// 7. 最终触发事件
		if (targetWord) {
			this.textHoverClick.emit([{ clientX: x, clientY: y }, targetWord]);
		}
	}

	// 新增：计算点到矩形的距离（中心距离，越近越优）
	private calculateDistanceToRect(x: number, y: number, rect: DOMRect): number {
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;
		// 欧几里得距离（也可以用曼哈顿距离：Math.abs(x - centerX) + Math.abs(y - centerY)）
		return Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
	}

	private handleMouseMove(event: MouseEvent): void {
		if (this.disable || !this.highlightElement) return;

		const hoverData = this.getHoverData(event);
		const fullText = this.el.nativeElement.innerText.trim();
		if (!hoverData && !fullText) {
			this.currentHighlightedWord = null;
			return;
		}

		if (fullText) {
			const cacheKey = `nlp_${fullText}`; // btoa 转 Base64 避免特殊字符问题
			const cachedNlpData = this.caches.getCachedData(cacheKey);
			const handleNlpData = (res)=>{
				if (res.output && res.output[0].items) {
					this.parts = res.output[0].items;

					let index = hoverData?.globalOffset || hoverData?.index || 0;
					let char = hoverData?.char || fullText[0];
					const items = res.output[0].items;

					// 核心修改3：鼠标移动时也基于视觉位置匹配
					for (let item of items) {
						const itemIndex = this.findWordIndexByVisualPosition(fullText, item.text, event.clientX, event.clientY);
						if (itemIndex !== -1 && index >= itemIndex && index < itemIndex + item.text.length) {
							const occurrence = this.countOccurrencesUpToIndex(fullText, item.text, index);
							this.highlightWord(item.text, occurrence, fullText, index);
							break;
						}
					}
				}
			};
			if (cachedNlpData){
				handleNlpData(cachedNlpData);
			} else {
				this.datas.post("NLP.analyze_chinese_text_array", [[fullText]]).subscribe((res: any) => {
					this.caches.cacheData(cacheKey, res);
					handleNlpData(res);
				});
			}

			if (hoverData) {
				this.hoverCallback.emit(hoverData);
			}
		}
		this.lastChar = hoverData?.char || fullText[0];
	}

	private highlightWord(word: string, occurrence: number, fullText: string, charIndex: number): void {
		if (!this.highlightElement) return;

		TextHoverDirective.activeHighlight = this;

		// 核心修改4：基于视觉位置高亮，而非纯文本索引
		const wordIndex = this.findNthOccurrence(fullText, word, occurrence);
		if (wordIndex === -1) return;

		// 获取词语的视觉位置
		const wordRect = this.getWordVisualRect(word, wordIndex);
		if (!wordRect) return;

		this.currentHighlightedWord = word;
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

		// 使用视觉位置设置高亮框
		this.renderer.setStyle(this.highlightElement, 'display', 'block');
		this.renderer.setStyle(this.highlightElement, 'left', `${wordRect.left + scrollLeft}px`);
		this.renderer.setStyle(this.highlightElement, 'top', `${wordRect.top + scrollTop}px`);
		this.renderer.setStyle(this.highlightElement, 'width', `${wordRect.width}px`);
		this.renderer.setStyle(this.highlightElement, 'height', `${wordRect.height}px`);
	}

	private removeHighlight(): void {
		if (this.highlightElement) {
			this.renderer.setStyle(this.highlightElement, 'display', 'none');
		}
		this.currentHighlightedWord = null;
		if (TextHoverDirective.activeHighlight === this) {
			TextHoverDirective.activeHighlight = null;
		}
	}

	private countOccurrencesUpToIndex(text: string, word: string, charIndex: number): number {
		let count = 0;
		let currentIndex = 0;

		while (currentIndex <= charIndex) {
			const foundIndex = text.indexOf(word, currentIndex);
			if (foundIndex === -1 || foundIndex > charIndex) {
				break;
			}
			count++;
			currentIndex = foundIndex + word.length;
		}

		return count;
	}

	private findTextNode(element: Node, text: string): Node | null {
		const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
		let node: Node | null;

		while (node = walker.nextNode()) {
			if (node.textContent?.includes(text)) {
				return node;
			}
		}
		return null;
	}

	private findNthOccurrence(text: string, word: string, n: number): number {
		let count = 0;
		let index = -1;

		while (count < n) {
			index = text.indexOf(word, index + 1);
			if (index === -1) return -1;
			count++;
		}

		return index;
	}

	private getLeaveData(event: MouseEvent | TouchEvent): any {
		return {
			event,
			char: this.lastChar || '',
			index: -1,
			fullText: ''
		};
	}

	private getHoverData(event: MouseEvent): any {
		let caretPos = null;
		if (document.caretPositionFromPoint) {
			caretPos = document.caretPositionFromPoint(event.clientX, event.clientY);
		} else if ((document as any).caretRangeFromPoint) {
			caretPos = (document as any).caretRangeFromPoint(event.clientX, event.clientY);
		}

		if (!caretPos) {
			const fullText = this.el.nativeElement.innerText.trim();
			if (fullText) {
				return {
					event,
					char: fullText[0] || '',
					index: 0,
					globalOffset: 0, // 新增：全局偏移量
					fullText: fullText
				};
			}
			return null;
		}

		const textNode = caretPos.startContainer || (caretPos as Range).startContainer;
		if (!textNode) {
			return null;
		}

		let localOffset = caretPos.offset || (caretPos as Range).startOffset;

		if (textNode.nodeType !== Node.TEXT_NODE) {
			const fullText = this.el.nativeElement.innerText.trim();
			if (fullText) {
				return {
					event,
					char: fullText[0] || '',
					index: 0,
					globalOffset: 0,
					fullText: fullText
				};
			}
			return null;
		}

		let el: any = event.target;
		el = el.parentElement ? el.parentElement : el;

		if (el.querySelector('a[href]')) {
			return null;
		}

		// 核心修改5：计算全局偏移量（包含所有前置文本节点）
		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		const nodeIndex = textNodes.findIndex(node => node === textNode);
		let globalOffset = 0;

		// 累加前置文本节点的长度
		for (let i = 0; i < nodeIndex; i++) {
			globalOffset += textNodes[i].textContent?.length || 0;
		}
		globalOffset += localOffset;

		const fullText = this.getAllTextFromNode(this.el.nativeElement);
		let char = fullText[globalOffset] || '';

		if (/[\u3000-\u303F\uff01-\uff60]/.test(char)) {
			char = fullText[globalOffset + 1] || fullText[0] || '';
		}

		return {
			event,
			char: char,
			index: localOffset,
			globalOffset: globalOffset, // 新增：返回全局偏移量
			fullText: fullText
		};
	}

	private getCharacterAtPointWithVisualPosition(x: number, y: number) {
		let caretPos: CaretPosition | Range | null = null;
		try {
			if (document.caretPositionFromPoint) {
				caretPos = document.caretPositionFromPoint(x, y);
			} else if ((document as any).caretRangeFromPoint) {
				caretPos = (document as any).caretRangeFromPoint(x, y);
			}
		} catch (e) {
			console.warn('获取CaretPosition失败:', e);
			return null;
		}

		if (!caretPos) return null;

		const offsetNode = (caretPos as CaretPosition).offsetNode || (caretPos as Range).startContainer;
		const localOffset = (caretPos as CaretPosition).offset || (caretPos as Range).startOffset;

		// 非文本节点直接返回空
		if (offsetNode.nodeType !== Node.TEXT_NODE) {
			return {
				element: offsetNode.parentElement,
				node: offsetNode,
				localOffset: localOffset,
				globalOffset: -1,
				character: '',
				rect: null
			};
		}

		const nodeText = (offsetNode.textContent || '').replace(/\s+/g, ' ').trim();
		const nodeTextLength = nodeText.length;
		if (nodeTextLength === 0) return null;

		// 精准计算全局偏移量（统一文本格式，避免换行/空格导致的偏差）
		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		const nodeIndex = textNodes.findIndex(node => node === offsetNode);
		let globalOffset = 0;

		// 累加前置节点的文本长度（统一格式后）
		for (let i = 0; i < nodeIndex; i++) {
			const prevNodeText = (textNodes[i].textContent || '').replace(/\s+/g, ' ').trim();
			globalOffset += prevNodeText.length;
		}

		// 精准匹配点击的字符（仅匹配当前offset，不扩展）
		let clickedCharIndex = localOffset;
		// 安全校验：确保索引在节点文本范围内
		clickedCharIndex = Math.max(0, Math.min(clickedCharIndex, nodeTextLength - 1));

		// 获取字符的视觉矩形（精准到单个字符）
		const range = document.createRange();
		range.setStart(offsetNode, clickedCharIndex);
		range.setEnd(offsetNode, clickedCharIndex + 1);
		const charRect = range.getBoundingClientRect();

		// 最终全局偏移量
		globalOffset += clickedCharIndex;
		// 点击的字符（从统一格式的节点文本中取）
		const charClicked = nodeText[clickedCharIndex] || '';

		return {
			element: offsetNode.parentElement,
			node: offsetNode,
			localOffset: clickedCharIndex,
			globalOffset: globalOffset,
			character: charClicked,
			rect: charRect
		};
	}

	private getWordVisualRect(word: string, startIndex: number): DOMRect | null {
		const fullText = this.el.nativeElement.innerText.replace(/\s+/g, ' ').trim();
		// 安全校验
		if (!word || startIndex < 0 || startIndex + word.length > fullText.length) {
			return null;
		}

		// 重新遍历文本节点，精准定位词语所在的节点和偏移
		const textNodes = this.getAllTextNodes(this.el.nativeElement);
		let currentGlobalIndex = 0;
		let targetNode: Node | null = null;
		let nodeStartOffset = 0;
		let nodeEndOffset = 0;

		for (const node of textNodes) {
			if (node.nodeType !== Node.TEXT_NODE) continue;

			const nodeText = (node.textContent || '').replace(/\s+/g, ' ').trim();
			const nodeTextLength = nodeText.length;

			// 检查当前节点是否包含词语的起始位置
			if (startIndex >= currentGlobalIndex && startIndex < currentGlobalIndex + nodeTextLength) {
				targetNode = node;
				// 计算词语在当前节点内的起始/结束偏移
				nodeStartOffset = startIndex - currentGlobalIndex;
				nodeEndOffset = nodeStartOffset + word.length;
				// 防止超出节点文本长度
				nodeEndOffset = Math.min(nodeEndOffset, nodeTextLength);
				break;
			}

			currentGlobalIndex += nodeTextLength;
		}

		if (!targetNode) return null;

		// 创建Range并获取精准的视觉矩形
		try {
			const range = document.createRange();
			range.setStart(targetNode, nodeStartOffset);
			range.setEnd(targetNode, nodeEndOffset);
			return range.getBoundingClientRect();
		} catch (e) {
			console.warn('创建Range失败:', e);
			return null;
		}
	}

	// 核心新增方法3：判断点是否在矩形内
	private isPointInRect(x: number, y: number, rect: DOMRect): boolean {
		return x >= rect.left - 5 &&
			x <= rect.right + 5 &&
			y >= rect.top - 5 &&
			y <= rect.bottom + 5;
	}

	// 核心新增方法4：通过视觉位置查找词语索引
	private findWordIndexByVisualPosition(fullText: string, word: string, x: number, y: number): number {
		let wordIndex = fullText.indexOf(word);
		while (wordIndex !== -1) {
			const rect = this.getWordVisualRect(word, wordIndex);
			if (rect && this.isPointInRect(x, y, rect)) {
				return wordIndex;
			}
			wordIndex = fullText.indexOf(word, wordIndex + word.length);
		}
		return -1;
	}

	// 保留原有方法（兼容）
	private getCharacterAtPoint(x: number, y: number) {
		return this.getCharacterAtPointWithVisualPosition(x, y);
	}

	private getAllTextFromNode(element: Node): string {
		let text = '';
		for (const node of Array.from(element.childNodes)) {
			if (node.nodeType === Node.TEXT_NODE) text += node.textContent || '';
			else if (node.nodeType === Node.ELEMENT_NODE) text += this.getAllTextFromNode(node);
		}
		return text;
	}

	private getAllTextNodes(element: Node): Node[] {
		const textNodes: Node[] = [];
		for (const node of Array.from(element.childNodes)) {
			if (node.nodeType === Node.TEXT_NODE) textNodes.push(node);
			else if (node.nodeType === Node.ELEMENT_NODE) textNodes.push(...this.getAllTextNodes(node));
		}
		return textNodes;
	}
}